import React from 'react';
import { X, MessageCircle, History, BookOpen, User, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

interface MessageLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSignUpClick: () => void;
  messageCount: number;
}

const MessageLimitModal: React.FC<MessageLimitModalProps> = ({
  isOpen,
  onClose,
  onSignUpClick,
  messageCount
}) => {
  if (!isOpen) return null;

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-start justify-center pt-8 px-4 pb-20 z-50 backdrop-blur-sm"
    >
      <motion.div 
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 relative"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-6 w-6" />
        </button>

        <div className="flex justify-center mb-8">
          <div className="h-20 w-20 bg-purple-50 rounded-2xl flex items-center justify-center">
            <User className="h-10 w-10 text-purple-600" />
          </div>
        </div>

        <h1 className="text-2xl font-semibold text-center mb-2">
          Join Space to Think
        </h1>
        
        <p className="text-gray-600 text-center mb-8">
          Create a free account to save your conversations and track your personal growth journey.
        </p>

        <div className="bg-purple-50 rounded-xl p-6 mb-6">
          <h2 className="font-medium text-gray-900 flex items-center mb-4">
            <Sparkles className="h-5 w-5 text-purple-600 mr-2" />
            What You'll Get
          </h2>
          <div className="space-y-4">
            <div className="flex items-start">
              <MessageCircle className="h-6 w-6 text-purple-600 mr-3 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">
                Unlimited conversations with Echo, your safe space to talk
              </span>
            </div>
            
            <div className="flex items-start">
              <History className="h-6 w-6 text-purple-600 mr-3 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">
                Access your complete conversation history anytime
              </span>
            </div>
            
            <div className="flex items-start">
              <BookOpen className="h-6 w-6 text-purple-600 mr-3 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">
                Personal reflections and insights from each conversation
              </span>
            </div>
          </div>
        </div>

        <button
          onClick={onSignUpClick}
          className="w-full py-3 bg-gradient-to-r from-[#a85ff7] to-[#e457b5] text-white rounded-lg hover:opacity-90 transition-opacity font-medium"
        >
          Create Free Account
        </button>
      </motion.div>
    </motion.div>
  );
};

export default MessageLimitModal;